import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchApi } from '@/common/lib/fetchApi'

export type PayPeriodState = 'drafting' | 'active' | 'closed'

export interface PayPeriod {
  id: number
  start_date: string
  end_date: string
  submission_deadline: string | null
  state: PayPeriodState | null
  payout_date: string | null
}

export interface TimeEntry {
  id: number
  date: string
  hours: number
  hours_worked: number
  holiday_hours: number
  vacation_hours: number
  standard_hours: number
  flsa_overtime_hours: number
  straight_overtime_hours: number
  clock_in_1: string | null
  clock_out_1: string | null
  clock_in_2: string | null
  clock_out_2: string | null
  comment: string | null
}

export interface AdjacentContextEntry {
  date: string
  hours_worked: number
  holiday_hours: number
  vacation_hours: number
  standard_hours: number
  flsa_overtime_hours: number
  straight_overtime_hours: number
  pay_period_start: string
  pay_period_end: string
  comment: string | null
}

export interface Timesheet {
  id: number
  employee_id: number
  pay_period: PayPeriod
  status: 'draft' | 'submitted' | 'approved' | 'rejected'
  rejection_reason: string | null
  approved_by_employee_id: number | null
  approved_by_name: string | null
  is_admin_override: boolean
  override_at: string | null
  entries: TimeEntry[]
  adjacent_context: AdjacentContextEntry[]
  work_days: number[]
  created_at: string
  updated_at: string
}

export interface TimeOffEntry {
  id: number
  employee_id: number
  type: string
  start_date: string
  end_date: string
  hours: number
  status: string
}

export interface Holiday {
  id: number
  name: string
  date: string
}

export function useCurrentTimesheet(periodStart?: string) {
  const params = periodStart ? `?period_start=${periodStart}` : ''
  return useQuery({
    queryKey: ['timesheet', 'current', periodStart ?? 'default'],
    queryFn: () => fetchApi<Timesheet>(`/api/timesheets/current${params}`),
  })
}

export function usePayPeriods() {
  return useQuery({
    queryKey: ['pay-periods'],
    queryFn: () => fetchApi<PayPeriod[]>('/api/pay-periods'),
  })
}

export function useTimeOff(employeeId: number | undefined, start: string, end: string) {
  return useQuery({
    queryKey: ['time-off', employeeId, start, end],
    queryFn: () =>
      fetchApi<TimeOffEntry[]>(`/api/employees/${employeeId}/time-off?start=${start}&end=${end}`),
    enabled: !!employeeId,
  })
}

export function useHolidays(start: string, end: string) {
  return useQuery({
    queryKey: ['holidays', start, end],
    queryFn: () => fetchApi<Holiday[]>(`/api/holidays?start=${start}&end=${end}`),
  })
}

export interface TimeEntryUpdatePayload {
  date: string
  hours_worked: number
  vacation_hours: number
  clock_in_1?: string | null
  clock_out_1?: string | null
  clock_in_2?: string | null
  clock_out_2?: string | null
  comment?: string | null
}

export function useUpdateEntries(timesheetId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entries: TimeEntryUpdatePayload[]) =>
      fetchApi<Timesheet>(`/api/timesheets/${timesheetId}/entries`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheet'] })
    },
  })
}

export function useSubmitTimesheet(timesheetId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      fetchApi<Timesheet>(`/api/timesheets/${timesheetId}/submit`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheet'] })
    },
  })
}
